// Payer Rules Engine Types

export type AuthRequirement = 'required' | 'not_required' | 'conditional';
export type SubmissionLaneType = 'fhir_pas' | 'portal' | 'fax' | 'phone';
export type InjuryType = 'bwc' | 'commercial';
export type SiteOfService = 'hospital' | 'asc' | 'office';

// Document types
export interface DocumentType {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: 'clinical' | 'administrative' | 'financial';
  is_common: boolean;
  created_at: string;
}

// Payer rule template (reusable patterns)
export interface PayerRuleTemplate {
  id: string;
  name: string;
  description?: string;
  injury_type: InjuryType;
  is_system_template: boolean;
  
  auth_requirement: AuthRequirement;
  primary_submission_lane: SubmissionLaneType;
  fallback_submission_lane?: SubmissionLaneType;
  
  sla_hours: number;
  initial_follow_up_hours: number;
  follow_up_cadence_hours: number;
  
  required_documents: string[];
  conditional_documents?: ConditionalDocument[] | null;
  
  created_at: string;
  updated_at: string;
}

// Conditional document requirement
export interface ConditionalDocument {
  document_code: string;
  condition: string; // Human-readable condition
  condition_logic?: Record<string, unknown>;
}

// Payer authorization rule
export interface PayerAuthRule {
  id: string;
  payer_id?: string;
  plan_name?: string;
  
  // Matching criteria
  cpt_code: string;
  modifier?: string;
  injury_type: InjuryType;
  site_of_service?: SiteOfService;
  diagnosis_pattern?: string;
  
  // Outputs
  auth_requirement: AuthRequirement;
  auth_condition_description?: string;
  auth_condition_logic?: Record<string, unknown>;
  
  // Submission
  primary_submission_lane: SubmissionLaneType;
  fallback_submission_lane?: SubmissionLaneType;
  payer_portal_url?: string;
  payer_fax_number?: string;
  payer_phone_number?: string;
  
  // SLA
  sla_hours: number;
  urgent_sla_hours?: number;
  initial_follow_up_hours: number;
  follow_up_cadence_hours: number;
  
  // Documentation
  required_documents: string[];
  conditional_documents?: ConditionalDocument[] | null;
  clinical_criteria?: string;
  special_instructions?: string;
  
  // Versioning
  version: number;
  effective_date: string;
  expiration_date?: string;
  is_active: boolean;
  superseded_by?: string;
  
  // Audit
  created_by?: string;
  created_at: string;
  updated_at: string;
  
  // Joined fields
  payer?: {
    id: string;
    name: string;
    payer_id: string;
  };
}

// Rules engine input
export interface RulesEngineInput {
  payer_id: string;
  plan_name?: string;
  injury_type: InjuryType;
  cpt_codes: string[];
  modifiers?: string[];
  site_of_service: SiteOfService;
  diagnosis_codes: string[];
  urgency?: 'routine' | 'urgent' | 'emergent';
}

// Rules engine output
export interface RulesEngineOutput {
  auth_required: AuthRequirement;
  auth_condition_description?: string;
  
  required_documents: DocumentRequirement[];
  missing_documents?: string[];
  
  recommended_lane: SubmissionLaneType;
  fallback_lane?: SubmissionLaneType;
  
  sla_hours: number;
  sla_deadline?: Date;
  initial_follow_up_hours: number;
  follow_up_cadence_hours: number;
  
  matched_rules: MatchedRule[];
  special_instructions?: string[];
  clinical_criteria?: string[];
  
  evaluation_timestamp: string;
}

// Matched rule with details
export interface MatchedRule {
  rule_id: string;
  cpt_code: string;
  modifier?: string;
  auth_requirement: AuthRequirement;
  source: 'specific_rule' | 'template' | 'default';
}

// Document requirement with status
export interface DocumentRequirement {
  code: string;
  name: string;
  description?: string;
  category?: string;
  is_required: boolean;
  is_conditional: boolean;
  condition_description?: string;
  is_provided: boolean;
}

// Rule form input for admin interface
export interface RuleFormInput {
  payer_id?: string;
  plan_name?: string;
  cpt_code: string;
  modifier?: string;
  injury_type: InjuryType;
  site_of_service?: SiteOfService;
  diagnosis_pattern?: string;
  
  auth_requirement: AuthRequirement;
  auth_condition_description?: string;
  
  primary_submission_lane: SubmissionLaneType;
  fallback_submission_lane?: SubmissionLaneType;
  payer_portal_url?: string;
  payer_fax_number?: string;
  payer_phone_number?: string;
  
  sla_hours: number;
  urgent_sla_hours?: number;
  initial_follow_up_hours: number;
  follow_up_cadence_hours: number;
  
  required_documents: string[];
  clinical_criteria?: string;
  special_instructions?: string;
  
  effective_date: string;
  expiration_date?: string;
}

// Display labels
export const AUTH_REQUIREMENT_LABELS: Record<AuthRequirement, string> = {
  required: 'Required',
  not_required: 'Not Required',
  conditional: 'Conditional',
};

export const AUTH_REQUIREMENT_COLORS: Record<AuthRequirement, string> = {
  required: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  not_required: 'bg-primary/10 text-primary border-primary/30',
  conditional: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
};

export const SUBMISSION_LANE_LABELS: Record<SubmissionLaneType, string> = {
  fhir_pas: 'FHIR PAS (Electronic)',
  portal: 'Payer Portal',
  fax: 'Fax',
  phone: 'Phone',
};

export const SUBMISSION_LANE_ICONS: Record<SubmissionLaneType, string> = {
  fhir_pas: 'Zap',
  portal: 'Globe',
  fax: 'Printer',
  phone: 'Phone',
};

export const INJURY_TYPE_LABELS: Record<InjuryType, string> = {
  bwc: 'Ohio BWC',
  commercial: 'Commercial',
};

export const SITE_OF_SERVICE_LABELS: Record<SiteOfService, string> = {
  hospital: 'Hospital',
  asc: 'Ambulatory Surgery Center',
  office: 'Office',
};

export const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  clinical: 'Clinical',
  administrative: 'Administrative',
  financial: 'Financial',
};
